import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  api,
  useCommunicationRealtime,
  useConversationRead,
  post,
  remove,
  useConversationMessages,
  useConversationComposer,
  useData,
  useSession,
} from '@yingbaba/api';
import {
  Button,
  Avatar,
  CommunicationAttachment,
  CommunicationConnectionNotice,
  Empty,
  ErrorNotice,
  PageTitle,
  QueryState,
  SupportCaseSummary,
  useToast,
  useConversationScroll,
} from '@yingbaba/ui';
import { CheckCircle2, FileUp, MessagesSquare, Send, UserCheck } from 'lucide-react';
import { datetime } from '@yingbaba/utils';

function categoryLabel(category: string) {
  return (
    (
      {
        general: '在线咨询',
        project: '项目协作',
        digital_purchase: '数字资源购买',
        membership: '会员服务',
      } as Record<string, string>
    )[category] || '业务咨询'
  );
}
import type { Conversation, SupportCasePage, SupportCaseView } from '@yingbaba/types';

type SupportProjectConversation = { conversation?: Conversation; joined: boolean };

function statusLabel(status: string) {
  return (
    (
      {
        new: '待接入',
        in_progress: '处理中',
        waiting_support: '等待客服',
        waiting_user: '等待用户',
        resolved: '已处理',
      } as Record<string, string>
    )[status] || status
  );
}

export function CustomerService() {
  const params = useParams<{ caseID?: string }>();
  const navigate = useNavigate();
  const routeID = params.caseID || '';
  const cases = useData<SupportCasePage>('/admin/support-cases');
  const routedCase = useData<SupportCaseView>(
    routeID ? `/admin/support-cases/${routeID}` : '/admin/support-cases/none',
    !!routeID,
  );
  const [olderCases, setOlderCases] = useState<SupportCaseView[]>([]);
  const [nextBeforeID, setNextBeforeID] = useState('');
  const [hasMoreCases, setHasMoreCases] = useState(false);
  const [loadingMoreCases, setLoadingMoreCases] = useState(false);
  const [selectedID, setSelectedID] = useState('');
  const allCases = useMemo(() => {
    const rows = [...(cases.data?.items || []), ...olderCases];
    const seen = new Set<string>();
    return rows.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, [cases.data?.items, olderCases]);
  const activeID = routeID || selectedID;
  const selected = useMemo(
    () =>
      routeID ? routedCase.data : allCases.find((item) => item.id === activeID) || allCases[0],
    [activeID, allCases, routeID, routedCase.data],
  );
  const projectConversation = useData<SupportProjectConversation>(
    selected?.project_id
      ? `/admin/support-cases/${selected.id}/project-conversation`
      : '/admin/support-cases/none/project-conversation',
    !!selected?.project_id,
  );
  const orderCase = useData<SupportCaseView>(
    `/admin/support-cases/${selected?.id || 'none'}`,
    !!selected?.order_id,
  );
  const [projectMode, setProjectMode] = useState(false);
  const activeConversation =
    projectMode && projectConversation.data?.joined && projectConversation.data.conversation
      ? projectConversation.data.conversation
      : selected?.conversation;
  const conversationID = activeConversation?.id || '';
  const messageFeed = useConversationMessages(
    conversationID
      ? `/admin/conversations/${conversationID}/messages`
      : '/admin/conversations/none/messages',
    !!conversationID,
    100,
  );
  const messages = messageFeed.latest;
  const session = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [claiming, setClaiming] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [joiningProject, setJoiningProject] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (olderCases.length > 0 || !cases.data) return;
    setNextBeforeID(cases.data.next_before_id || '');
    setHasMoreCases(cases.data.has_more);
  }, [cases.data, olderCases.length]);

  const accountID = session.data?.account.id || '';
  const platformRole = session.data?.account.platform_role || '';
  const mine = selected?.assigned_to === accountID;
  const canTakeOver = !selected?.assigned_to || mine || platformRole === 'super_admin';
  const canReply =
    !!selected &&
    mine &&
    selected.status !== 'resolved' &&
    (!projectMode || !!projectConversation.data?.joined);

  const {
    text,
    setText,
    sending,
    uploading,
    error: composerError,
    sendText,
    attach,
  } = useConversationComposer({
    conversationID,
    accountID,
    basePath: '/admin',
    canSend: canReply,
    fileDeletePath: '/admin/communication-files',
    listPath: '/admin/support-cases',
  });

  const actionScope = `${accountID}:${selected?.id || ''}`;
  const actionScopeRef = useRef(actionScope);
  actionScopeRef.current = actionScope;
  useEffect(() => {
    setError(null);
  }, [actionScope]);
  useEffect(() => {
    if (routedCase.data)
      setOlderCases((rows) =>
        rows.map((row) => (row.id === routedCase.data.id ? routedCase.data : row)),
      );
  }, [routedCase.data]);

  useEffect(() => {
    if (!routeID && !selectedID && allCases[0]) {
      setSelectedID(allCases[0].id);
    }
  }, [allCases, routeID, selectedID]);

  async function loadMoreCases() {
    if (!hasMoreCases || !nextBeforeID || loadingMoreCases) return;
    setLoadingMoreCases(true);
    setError(null);
    try {
      const page = await api<SupportCasePage>(
        `/admin/support-cases?before_id=${encodeURIComponent(nextBeforeID)}&limit=50`,
      );
      setOlderCases((current) => [...current, ...page.items]);
      setNextBeforeID(page.next_before_id || '');
      setHasMoreCases(page.has_more);
    } catch (e) {
      setError(e);
    } finally {
      setLoadingMoreCases(false);
    }
  }

  useEffect(() => {
    setProjectMode(false);
  }, [selected?.id]);

  const connection = useCommunicationRealtime({
    accountID,
    onEvent: (envelope) => {
      if (
        !['message.created', 'read.updated', 'connected', 'support.updated'].includes(envelope.type)
      )
        return;
      void qc.invalidateQueries({
        predicate: (query) => {
          const path = String(query.queryKey[0]);
          return envelope.type === 'connected'
            ? path.startsWith('/admin/support-cases')
            : path === '/admin/support-cases' ||
                path.startsWith('/admin/support-cases?') ||
                /^\/admin\/support-cases\/[^/?]+$/.test(path);
        },
      });
      if (envelope.type === 'support.updated') return;
      const id = envelope.payload?.conversation_id;
      void qc.invalidateQueries({
        predicate: (query) => {
          const path = String(query.queryKey[0]);
          return envelope.type === 'connected'
            ? path.startsWith('/admin/conversations/')
            : !!id && path.startsWith(`/admin/conversations/${id}/messages`);
        },
      });
    },
  });
  useConversationRead({
    accountID,
    path: `/admin/conversations/${conversationID}/read`,
    seq: messageFeed.items.at(-1)?.seq,
    enabled: !!conversationID,
    onRead: () =>
      void qc.invalidateQueries({
        predicate: (query) =>
          String(query.queryKey[0]) === '/admin/support-cases' ||
          String(query.queryKey[0]).startsWith('/admin/support-cases?'),
      }),
  });

  const { viewportRef, onScroll, loadEarlier } = useConversationScroll(
    conversationID,
    messageFeed.items,
    accountID,
    messageFeed.loadOlder,
  );

  async function joinProjectConversation() {
    if (!selected || joiningProject) return;
    setJoiningProject(true);
    setError(null);
    try {
      await post(`/admin/support-cases/${selected.id}/project-conversation/join`, {});
      await qc.invalidateQueries();
      if (actionScopeRef.current !== actionScope) return;
      setProjectMode(true);
      toast('已加入项目群；仅从加入时刻开始接收项目消息');
    } catch (e) {
      if (actionScopeRef.current === actionScope) setError(e);
    } finally {
      setJoiningProject(false);
    }
  }

  async function leaveProjectConversation() {
    if (!selected || joiningProject) return;
    setJoiningProject(true);
    setError(null);
    try {
      await remove(`/admin/support-cases/${selected.id}/project-conversation`);
      await qc.invalidateQueries();
      if (actionScopeRef.current !== actionScope) return;
      setProjectMode(false);
      toast('已退出项目群');
    } catch (e) {
      if (actionScopeRef.current === actionScope) setError(e);
    } finally {
      setJoiningProject(false);
    }
  }

  async function claim() {
    if (!selected || claiming) return;
    setClaiming(true);
    setError(null);
    try {
      await post(`/admin/support-cases/${selected.id}/claim`, {});
      await qc.invalidateQueries();
      if (actionScopeRef.current !== actionScope) return;
      toast(selected.assigned_to ? '工单已接管' : '工单已认领');
    } catch (e) {
      if (actionScopeRef.current === actionScope) setError(e);
    } finally {
      setClaiming(false);
    }
  }

  async function setStatus(status: 'resolved' | 'in_progress') {
    if (!selected || updatingStatus) return;
    setUpdatingStatus(true);
    setError(null);
    try {
      await post(`/admin/support-cases/${selected.id}/status`, { status });
      await qc.invalidateQueries();
      if (actionScopeRef.current !== actionScope) return;
      toast(status === 'resolved' ? '工单已处理' : '工单已重新打开');
    } catch (e) {
      if (actionScopeRef.current === actionScope) setError(e);
    } finally {
      setUpdatingStatus(false);
    }
  }

  function send(event: FormEvent) {
    event.preventDefault();
    void sendText();
  }

  async function sendAttachment(file?: File) {
    if (await attach(file)) toast('附件已发送');
  }

  return (
    <>
      <PageTitle
        title="客服中心"
        description="客服工单与实时会话统一处理。查看不会自动认领；回复前需要明确认领工单。"
      />
      <ErrorNotice error={error || composerError || messageFeed.error || routedCase.error} />
      <CommunicationConnectionNotice state={connection} />
      <QueryState query={cases}>
        {!allCases.length && !routeID ? (
          <Empty
            title="暂无待处理咨询"
            description="用户从公开站或工作台发起咨询后，会以独立工单显示在这里。"
          />
        ) : (
          <div className="communication-shell admin-communication">
            <aside className="communication-list" aria-label="客服工单列表">
              {allCases.map((item) => (
                <button
                  type="button"
                  className={`communication-conversation ${selected?.id === item.id ? 'active' : ''}`}
                  key={item.id}
                  onClick={() => {
                    navigate(`/support-cases/${item.id}`);
                  }}
                >
                  <div className="row spread">
                    <span className="communication-kind">{statusLabel(item.status)}</span>
                    <span className="small muted">{categoryLabel(item.category)}</span>
                  </div>
                  <strong>{item.subject}</strong>
                  <p>
                    {item.requester_name || '用户'}
                    {item.project_title ? ` · ${item.project_title}` : ''}
                  </p>
                  <time>{datetime(item.updated_at)}</time>
                </button>
              ))}
              {hasMoreCases ? (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={loadingMoreCases}
                  onClick={loadMoreCases}
                >
                  {loadingMoreCases ? '加载中…' : '加载更多工单'}
                </Button>
              ) : null}
            </aside>
            <section className="communication-panel">
              {selected ? (
                <>
                  <header className="communication-header">
                    <div>
                      <span className="eyebrow">
                        {projectMode ? '项目群协作' : statusLabel(selected.status)} ·{' '}
                        {categoryLabel(selected.category)}
                      </span>
                      <h2>
                        {projectMode
                          ? selected.project_title || selected.subject
                          : selected.subject}
                      </h2>
                      <p className="small muted">
                        {selected.requester_name || '用户'}
                        {selected.assigned_to_name
                          ? ` · 负责人：${selected.assigned_to_name}`
                          : ' · 尚未认领'}
                      </p>
                    </div>
                    <div className="row gap-sm support-case-actions">
                      {selected.project_id && mine && selected.status !== 'resolved' ? (
                        projectConversation.data?.joined ? (
                          <>
                            <Button
                              variant="secondary"
                              onClick={() => setProjectMode((value) => !value)}
                            >
                              <MessagesSquare size={16} /> {projectMode ? '工单会话' : '项目群'}
                            </Button>
                            <Button
                              variant="secondary"
                              loading={joiningProject}
                              onClick={() => void leaveProjectConversation()}
                            >
                              退出项目群
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="secondary"
                            loading={joiningProject}
                            onClick={() => void joinProjectConversation()}
                          >
                            <MessagesSquare size={16} /> 加入项目群
                          </Button>
                        )
                      ) : null}
                      {!mine && canTakeOver && selected.status !== 'resolved' ? (
                        <Button variant="secondary" loading={claiming} onClick={claim}>
                          <UserCheck size={16} /> {selected.assigned_to ? '接管' : '认领'}
                        </Button>
                      ) : null}
                      {mine && selected.status !== 'resolved' ? (
                        <Button
                          variant="secondary"
                          loading={updatingStatus}
                          onClick={() => void setStatus('resolved')}
                        >
                          <CheckCircle2 size={16} /> 标记已处理
                        </Button>
                      ) : null}
                      {mine && selected.status === 'resolved' ? (
                        <Button
                          variant="secondary"
                          loading={updatingStatus}
                          onClick={() => void setStatus('in_progress')}
                        >
                          重新打开
                        </Button>
                      ) : null}
                    </div>
                  </header>
                  {!projectMode && selected.order_id && (
                    <>
                      <ErrorNotice error={orderCase.error} />
                      <SupportCaseSummary value={orderCase.isSuccess ? orderCase.data : selected} />
                    </>
                  )}
                  <QueryState query={messages}>
                    <div
                      className="communication-messages"
                      ref={viewportRef}
                      onScroll={onScroll}
                      aria-live="polite"
                    >
                      {messageFeed.hasMore ? (
                        <Button
                          variant="secondary"
                          loading={messageFeed.loadingOlder}
                          onClick={() => void loadEarlier()}
                        >
                          加载更早消息
                        </Button>
                      ) : null}
                      {messageFeed.items.map((message) => {
                        const ownMessage = message.sender_id === accountID;
                        return (
                          <article
                            className={`communication-message ${ownMessage ? 'mine' : ''}`}
                            key={message.id}
                          >
                            <div className="communication-message-meta">
                              <Avatar
                                name={message.sender_name || '成员'}
                                src={message.sender_avatar_url}
                                size={28}
                              />
                              <strong>{ownMessage ? '我' : message.sender_name || '用户'}</strong>
                              <time>{datetime(message.created_at)}</time>
                            </div>
                            {message.content &&
                            !message.attachments.some(
                              (file) => file.file_name === message.content,
                            ) ? (
                              <p>{message.content}</p>
                            ) : null}
                            {message.attachments.map((file) => (
                              <CommunicationAttachment
                                key={file.id}
                                file={file}
                                previewPath={`/admin/communication-files/${file.id}/preview`}
                                thumbnailPath={`/admin/communication-files/${file.id}/thumbnail`}
                                downloadPath={`/admin/communication-files/${file.id}/download`}
                              />
                            ))}
                          </article>
                        );
                      })}
                      {!messageFeed.items.length ? (
                        <div className="communication-empty">
                          {projectMode
                            ? '加入项目群后，从加入时刻开始显示项目消息。'
                            : '该工单还没有消息。'}
                        </div>
                      ) : null}
                    </div>
                  </QueryState>
                  {canReply ? (
                    <form className="communication-composer" onSubmit={send}>
                      <label className="btn btn-secondary communication-upload">
                        <FileUp size={16} /> {uploading ? '上传中…' : '附件'}
                        <input
                          type="file"
                          disabled={uploading || sending}
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            void sendAttachment(file);
                            event.currentTarget.value = '';
                          }}
                        />
                      </label>
                      <input
                        className="input grow"
                        value={text}
                        onChange={(event) => setText(event.target.value)}
                        maxLength={8000}
                        placeholder="回复用户…"
                        aria-label="客服消息"
                      />
                      <Button loading={sending} disabled={uploading || !text.trim()}>
                        <Send size={16} /> 发送
                      </Button>
                    </form>
                  ) : (
                    <div className="communication-composer small muted">
                      {selected.status === 'resolved'
                        ? '工单已处理；需要继续沟通时请先重新打开。'
                        : selected.assigned_to
                          ? `该工单由 ${selected.assigned_to_name || '其他客服'} 处理。`
                          : '请先认领工单，再回复用户。'}
                    </div>
                  )}
                </>
              ) : null}
            </section>
          </div>
        )}
      </QueryState>
    </>
  );
}
